// SplitTimeModal.js
import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, DollarSign, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

const SplitTimeModal = ({ 
  isOpen, 
  onClose, 
  entry, 
  onSplitComplete,
  projects = [],
  clients = [] 
}) => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  
  const [splits, setSplits] = useState([
    {
      id: 1,
      description: '',
      duration_hours: entry?.duration_hours || 0,
      is_billable: entry?.is_billable || false,
      hourly_rate: entry?.hourly_rate || 0
    }
  ]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Get project and client info
  const project = projects.find(p => p.id === entry?.project_id);
  const client = clients.find(c => c.id === entry?.client_id);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen && entry) {
      setSplits([
        {
          id: 1,
          description: `${entry.task_name} - Billable portion`,
          duration_hours: Math.round(entry.duration_hours * 0.75 * 4) / 4, // 75% default, rounded to quarter hours
          is_billable: true,
          hourly_rate: entry.hourly_rate || project?.hourly_rate || 0
        },
        {
          id: 2,
          description: `${entry.task_name} - Non-billable portion`,
          duration_hours: Math.round(entry.duration_hours * 0.25 * 4) / 4, // 25% default, rounded to quarter hours
          is_billable: false,
          hourly_rate: 0
        }
      ]);
      setError(null);
      setSuccess(false);
    }
  }, [isOpen, entry, project]);

  const addSplit = () => {
    setSplits(prev => [...prev, {
      id: Math.max(...prev.map(s => s.id)) + 1,
      description: '',
      duration_hours: 0,
      is_billable: false,
      hourly_rate: entry?.hourly_rate || project?.hourly_rate || 0
    }]);
  };

  const removeSplit = (id) => {
    if (splits.length > 1) {
      setSplits(prev => prev.filter(split => split.id !== id));
    }
  };

  const updateSplit = (id, field, value) => {
    setSplits(prev => prev.map(split => 
      split.id === id 
        ? { 
            ...split, 
            [field]: field === 'duration_hours' ? parseFloat(value) || 0 : value,
            // Auto-set hourly rate to 0 for non-billable
            ...(field === 'is_billable' && !value ? { hourly_rate: 0 } : {}),
            // Auto-set hourly rate to entry rate for billable
            ...(field === 'is_billable' && value ? { hourly_rate: entry?.hourly_rate || project?.hourly_rate || 0 } : {})
          }
        : split
    ));
  };

  // Calculate totals
  const totalDuration = splits.reduce((sum, split) => sum + (split.duration_hours || 0), 0);
  const totalBillableHours = splits.reduce((sum, split) => 
    sum + (split.is_billable ? (split.duration_hours || 0) : 0), 0);
  const totalNonBillableHours = splits.reduce((sum, split) => 
    sum + (!split.is_billable ? (split.duration_hours || 0) : 0), 0);
  const totalBillableRevenue = splits.reduce((sum, split) => 
    sum + (split.is_billable ? (split.duration_hours || 0) * (split.hourly_rate || 0) : 0), 0);

  // Validation
  const isValid = () => {
    // Check if total duration matches original
    if (Math.abs(totalDuration - entry.duration_hours) > 0.01) {
      return false;
    }
    
    // Check if all splits have descriptions
    if (splits.some(split => !split.description.trim())) {
      return false;
    }
    
    // Check if all splits have positive duration
    if (splits.some(split => split.duration_hours <= 0)) {
      return false;
    }
    
    return true;
  };

  const handleApplySplit = async () => {
    if (!isValid()) {
      setError('Please ensure all splits have descriptions and the total duration matches the original entry.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare splits data for the database function
      const splitsData = splits.map(split => ({
        description: split.description.trim(),
        duration_hours: split.duration_hours,
        is_billable: split.is_billable,
        hourly_rate: split.hourly_rate
      }));

      // Call the database function to apply the split
      const { data, error: dbError } = await supabase.rpc('apply_time_entry_split', {
        p_entry_id: entry.id,
        p_splits: splitsData,
        p_split_by: user.id
      });

      if (dbError) {
        throw new Error(dbError.message);
      }

      setSuccess(true);
      
      // Wait a moment to show success message
      setTimeout(() => {
        onSplitComplete && onSplitComplete(entry.id, data);
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Error applying split:', err);
      setError(`Failed to apply split: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  const formatCurrency = (amount) => `$${amount.toFixed(2)}`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Split Time Entry</h3>
            <p className="text-sm text-gray-600 mt-1">
              Divide this entry into billable and non-billable portions
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Original Entry Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-gray-900 mb-3">Original Entry</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
              <div>
                <p><strong>Task:</strong> {entry.task_name}</p>
                <p><strong>Client:</strong> {client?.name || 'Unknown'}</p>
                <p><strong>Project:</strong> {project?.name || 'Unknown'}</p>
              </div>
              <div>
                <p><strong>Duration:</strong> {formatTime(entry.duration_hours)}</p>
                <p><strong>Rate:</strong> {formatCurrency(entry.hourly_rate || 0)}/hr</p>
                <p><strong>Originally:</strong> 
                  <span className={`ml-1 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    entry.is_billable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {entry.is_billable ? 'Billable' : 'Non-billable'}
                  </span>
                </p>
              </div>
            </div>
            {entry.description && (
              <p className="mt-2 text-sm text-gray-600">
                <strong>Description:</strong> {entry.description}
              </p>
            )}
          </div>

          {/* Split Configuration */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">Time Breakdown</h4>
              <button
                onClick={addSplit}
                className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
                disabled={loading}
              >
                <Plus className="w-4 h-4" />
                <span>Add Split</span>
              </button>
            </div>

            {splits.map((split, index) => (
              <div key={split.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-medium text-gray-800">
                    Split {index + 1}
                    {split.is_billable && <DollarSign className="w-4 h-4 inline ml-1 text-green-600" />}
                  </h5>
                  {splits.length > 1 && (
                    <button
                      onClick={() => removeSplit(split.id)}
                      className="text-red-400 hover:text-red-600"
                      disabled={loading}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description *
                    </label>
                    <input
                      type="text"
                      value={split.description}
                      onChange={(e) => updateSplit(split.id, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Describe this portion of work..."
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (hours) *
                    </label>
                    <input
                      type="number"
                      step="0.25"
                      min="0"
                      max={entry.duration_hours}
                      value={split.duration_hours}
                      onChange={(e) => updateSplit(split.id, 'duration_hours', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Status
                    </label>
                    <select
                      value={split.is_billable ? 'billable' : 'non-billable'}
                      onChange={(e) => updateSplit(split.id, 'is_billable', e.target.value === 'billable')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={loading}
                    >
                      <option value="billable">Billable</option>
                      <option value="non-billable">Non-billable</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hourly Rate
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={split.hourly_rate}
                      onChange={(e) => updateSplit(split.id, 'hourly_rate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={loading || !split.is_billable}
                    />
                  </div>
                </div>

                {/* Split Summary */}
                <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-600">
                  <div className="flex justify-between items-center">
                    <span>Duration: {formatTime(split.duration_hours)}</span>
                    <span>
                      Revenue: {split.is_billable ? formatCurrency(split.duration_hours * split.hourly_rate) : '$0.00'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h5 className="font-medium text-blue-900 mb-3 flex items-center">
              <CheckCircle className="w-4 h-4 mr-1" />
              Split Summary
            </h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <p className="font-medium text-blue-900">Total Duration</p>
                <p className={`text-lg ${Math.abs(totalDuration - entry.duration_hours) > 0.01 ? 'text-red-600' : 'text-blue-700'}`}>
                  {formatTime(totalDuration)}
                </p>
                <p className="text-xs text-blue-600">
                  of {formatTime(entry.duration_hours)}
                </p>
              </div>
              <div className="text-center">
                <p className="font-medium text-blue-900">Billable</p>
                <p className="text-lg text-green-600">
                  {formatTime(totalBillableHours)}
                </p>
                <p className="text-xs text-blue-600">
                  {formatCurrency(totalBillableRevenue)}
                </p>
              </div>
              <div className="text-center">
                <p className="font-medium text-blue-900">Non-billable</p>
                <p className="text-lg text-gray-600">
                  {formatTime(totalNonBillableHours)}
                </p>
                <p className="text-xs text-blue-600">$0.00</p>
              </div>
              <div className="text-center">
                <p className="font-medium text-blue-900">Total Splits</p>
                <p className="text-lg text-blue-700">{splits.length}</p>
                <p className="text-xs text-blue-600">components</p>
              </div>
            </div>
            
            {/* Validation Status */}
            <div className="mt-3 flex items-center justify-center">
              {Math.abs(totalDuration - entry.duration_hours) > 0.01 && (
                <div className="flex items-center text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  Duration mismatch: {formatTime(totalDuration - entry.duration_hours)} difference
                </div>
              )}
              {Math.abs(totalDuration - entry.duration_hours) <= 0.01 && (
                <div className="flex items-center text-green-600 text-sm">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Duration matches original entry
                </div>
              )}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Success Display */}
          {success && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
                <p className="text-sm text-green-700">
                  Split applied successfully! The entry has been divided into {splits.length} components.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleApplySplit}
            disabled={loading || !isValid() || success}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading && <Clock className="w-4 h-4 animate-spin" />}
            <span>{loading ? 'Applying Split...' : 'Apply Split'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SplitTimeModal;